<template>
    <div class="truck-map-container">
        <!-- BUSCADOR / FILTRO PARA EL MAPA -->
        <div class="mb-3">
            <div class="d-flex align-center justify-space-between mb-1">
                <span class="text-caption font-weight-black text-uppercase tracking-wider text-cyan-accent-1">
                    Filtrar Mapa
                </span>
                <v-chip
                    v-if="searchQuery"
                    size="x-small"
                    color="cyan"
                    variant="tonal"
                    class="font-weight-bold"
                    style="height: 16px; font-size: 0.58rem;"
                >
                    Filtro Activo
                </v-chip>
            </div>

            <v-text-field
                v-model="searchQuery"
                density="compact"
                variant="outlined"
                hide-details
                placeholder="Buscar por Nombre, IP, Modelo, Tipo (Acceso, Cliente) o Estado (En línea, Alerta, Alarma, OFF)..."
                prepend-inner-icon="mdi-magnify"
                clearable
                class="neon-search-field"
            ></v-text-field>
        </div>

        <!-- CONTENEDOR DEL MAPA SATELITAL -->
        <div ref="wrapperRef" class="topology-wrapper">
            <div ref="mapTruckContainer" class="mapTruckContainer"></div>

            <v-btn
                icon
                size="small"
                color="slate-800"
                class="fullscreen-btn"
                @click="toggleFullscreen"
            >
                <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
            </v-btn>
        </div>

        <!-- SECCIÓN INFERIOR: EQUIPOS SIN UBICACIÓN GPS -->
        <div v-if="equiposSinGps.length > 0" class="no-gps-section mt-2 pa-2">
            <div class="d-flex align-center mb-2 ga-2">
                <v-icon size="x-small" color="warning">mdi-map-marker-off</v-icon>
                <span class="no-gps-title font-weight-bold">
                    Equipos sin ubicación GPS
                </span>
                <v-chip size="x-small" color="grey-darken-2" variant="flat" class="px-1" style="height: 16px; font-size: 0.65rem;">
                    {{ equiposSinGps.length }}
                </v-chip>
            </div>

            <div class="no-gps-scroll-container d-flex ga-2 pb-1">
                <div
                    v-for="equipo in equiposSinGps"
                    :key="equipo.ip"
                    class="no-gps-card pa-1 px-2"
                    @click="$emit('select-device', equipo)"
                >
                    <div class="operability-bar-wrapper mb-1">
                        <div class="operability-stacked-bar">
                            <div class="bar-segment" style="background-color: #00E676;" :style="{ width: getOperabilityPercentages(equipo.ip).ok + '%' }"></div>
                            <div class="bar-segment" style="background-color: #FFEA00;" :style="{ width: getOperabilityPercentages(equipo.ip).alert + '%' }"></div>
                            <div class="bar-segment" style="background-color: #FF3D00;" :style="{ width: getOperabilityPercentages(equipo.ip).alarm + '%' }"></div>
                            <div class="bar-segment" style="background-color: #9E9E9E;" :style="{ width: getOperabilityPercentages(equipo.ip).down + '%' }"></div>
                        </div>
                    </div>

                    <div class="d-flex justify-space-between align-center ga-2">
                        <span class="device-name font-weight-bold text-truncate" :style="{ color: getLatencyColor(equipo.latencia) }">
                            {{ equipo.name }}
                        </span>
                        <span class="any-badge font-weight-bold" :style="{ color: getLatencyColor(equipo.latencia) }">
                            {{ equipo.latencia < 0 ? 'OFF' : `${equipo.latencia}ms` }}
                        </span>
                    </div>

                    <div class="d-flex justify-space-between align-center mt-1 style-details">
                        <span class="device-model text-truncate">{{ getDeviceModel(equipo.anotacion) }}</span>
                        <span class="ip-text">{{ equipo.ip }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import maplibregl, { Map, Popup, Marker, LngLatLike } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

// IMPORTACIÓN DE IMÁGENES
import camionIcon from '@/assets/Icons/camion.jpg';
import solarTrailerIcon from '@/assets/Icons/solartrailer.jpg';

import CONSTMAPS from '@/constants/maps';
import { getDeviceModel } from '@/utils/SmartLink/device';
import { LATENCY } from '@/interfaces/SmartLink/Topology';
import { OPERABILITY } from '@/interfaces/SmartLink/Equipos';

interface Props {
    trucks?: LATENCY[];
    operabilityData?: OPERABILITY[];
}

const props = withDefaults(defineProps<Props>(), {
    trucks: () => [],
    operabilityData: () => []
});

const emit = defineEmits(['select-device']);

const wrapperRef = ref<HTMLElement | null>(null);
const mapTruckContainer = ref<HTMLElement | null>(null);
const isFullscreen = ref(false);
const searchQuery = ref('');

let mapInstance: Map | null = null;
let activePopup: Popup | null = null;
let currentMarkers: Marker[] = [];

const centerMap = ref<LngLatLike>([
    CONSTMAPS.longitud.value,
    CONSTMAPS.latitud.value,
]);

// EVALUAR COINCIDENCIA DE ESTADO SEGÚN LA LATENCIA
function checkStatusMatch(latencia: number, query: string): boolean {
    const q = query.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
    if (!q) return false;

    if (latencia < 0) {
        const terms = ['off', 'sin servicio', 'sin serv', 'down', 'desconectado', 'fuera de servicio'];
        return terms.some(t => t.includes(q));
    }
    if (latencia <= 100) {
        const terms = ['ok', 'en linea', 'optimo', 'excelente', 'bueno', 'online'];
        return terms.some(t => t.includes(q));
    }
    if (latencia <= 500) {
        const terms = ['alerta', 'warning', 'leve'];
        return terms.some(t => t.includes(q));
    }
    const terms = ['alarma', 'critico', 'error'];
    return terms.some(t => t.includes(q));
}

// FILTRADO INTERNO POR BÚSQUEDA (NOMBRE, IP, MODELO, TIPO, SUBTIPO Y ESTADO)
const searchedTrucks = computed(() => {
    if (!searchQuery.value) return props.trucks || [];
    const query = searchQuery.value.toLowerCase().trim();

    return (props.trucks || []).filter(item => {
        const nameMatch = item.name?.toLowerCase().includes(query);
        const ipMatch = item.ip?.toLowerCase().includes(query);
        const modelMatch = getDeviceModel(item.anotacion)?.toLowerCase().includes(query);
        
        const subtipoMatch = item.subtipo?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(query);
        const tipoMatch = item.tipo?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(query);
        
        const statusMatch = checkStatusMatch(item.latencia, query);

        return nameMatch || ipMatch || modelMatch || subtipoMatch || tipoMatch || statusMatch;
    });
});

// FILTRADO PARA MOSTRAR CLIENTES Y ACCESOS
const filteredVisibleDevices = computed(() => {
    const safeTrucks = searchedTrucks.value;

    return safeTrucks.filter(item => {
        if (!item?.subtipo) return false;

        const subtipoNorm = item.subtipo.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

        const isCliente = subtipoNorm === 'cliente';
        const isAcceso = subtipoNorm === 'acceso';

        return isCliente || isAcceso;
    });
});

const uniqueItems = computed(() => {
    return filteredVisibleDevices.value.filter(
        (item, index, self) => index === self.findIndex((t) => t.ip === item.ip)
    );
});

const equiposConGps = computed(() => {
    return uniqueItems.value.filter(
        (e) =>
            e.latitud !== undefined &&
            e.longitud !== undefined &&
            Number(e.latitud) !== -1 &&
            Number(e.longitud) !== -1 &&
            Number(e.latitud) !== 0 &&
            Number(e.longitud) !== 0
    );
});

const equiposSinGps = computed(() => {
    return uniqueItems.value.filter(
        (e) =>
            e.latitud === undefined ||
            e.longitud === undefined ||
            Number(e.latitud) === -1 ||
            Number(e.longitud) === -1 ||
            Number(e.latitud) === 0 ||
            Number(e.longitud) === 0
    );
});

function getLatencyColor(latencia: number): string {
    if (latencia < 0) return '#9E9E9E';   
    if (latencia <= 100) return '#00E676'; 
    if (latencia <= 500) return '#FFEA00'; 
    return '#FF3D00';                      
}

function getOperabilityPercentages(ip: string) {
    const safeData = props.operabilityData || [];
    const item = safeData.find(op => op.ip === ip);
    if (!item) return { ok: 0, alert: 0, alarm: 0, down: 100 };
    const total = (item.ok || 0) + (item.alert || 0) + (item.alarm || 0) + (item.down || 0);
    if (total === 0) return { ok: 0, alert: 0, alarm: 0, down: 100 };
    return { 
        ok: ((item.ok || 0) / total) * 100, 
        alert: ((item.alert || 0) / total) * 100, 
        alarm: ((item.alarm || 0) / total) * 100, 
        down: ((item.down || 0) / total) * 100 
    };
}

function formatShortDate(dateStr?: string) {
    if (!dateStr) return 'N/A';
    const parts = dateStr.trim().split(' ');
    return parts.length < 2 ? dateStr : `${parts[0]} ${parts[1].substring(0, 5)}`;
}

const clearMarkers = () => {
    currentMarkers.forEach((marker) => marker.remove());
    currentMarkers = [];
};

const renderMapMarkers = () => {
    if (!mapInstance) return;
    clearMarkers();

    equiposConGps.value.forEach((equipo) => {
        const latColor = getLatencyColor(equipo.latencia);
        const modelText = getDeviceModel(equipo.anotacion);
        const dateText = formatShortDate(equipo.fecha);
        const op = getOperabilityPercentages(equipo.ip);
        const latDisplay = equipo.latencia < 0 ? "OFF" : `${equipo.latencia}ms`;

        const subtipoNorm = equipo.subtipo?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
        const isAcceso = subtipoNorm === 'acceso';
        const iconSrc = isAcceso ? solarTrailerIcon : camionIcon;
        const iconAlt = isAcceso ? 'Acceso (Solar Trailer)' : 'Cliente (Camión)';

        const el = document.createElement("div");
        el.className = "custom-icon-marker";

        el.innerHTML = `
            <img src="${iconSrc}" class="device-img" alt="${iconAlt}" />
            <div class="marker-name-only" style="color: ${latColor};">
                ${equipo.name}
            </div>
        `;

        el.addEventListener("click", (e) => {
            e.stopPropagation();
            emit("select-device", equipo);
        });

        el.addEventListener("mouseenter", () => {
            if (!mapInstance) return;

            const htmlContent = `
                <div style="font-family: sans-serif; padding: 4px; font-size: 0.78rem; color: #0F172A;">
                    <b style="font-size: 0.85rem; color: #0F172A;">${equipo.name}</b><br/>
                    <span style="color: #0284C7;"><b>Modelo:</b> ${modelText}</span><br/>
                    <span><b>IP:</b> ${equipo.ip}</span><br/>
                    <span><b>Tipo:</b> <span style="text-transform: capitalize;">${equipo.subtipo}</span></span><br/>
                    <span><b>Latencia:</b> ${latDisplay}</span><br/>
                    <span><b>Última act.:</b> ${dateText}</span>
                    <hr style="border-top: 1px solid #CBD5E1; margin: 4px 0;" />
                    <b>Operatividad:</b><br/>
                    <span style="color: #16A34A;">● Excelente: ${op.ok.toFixed(1)}%</span><br/>
                    <span style="color: #CA8A04;">● Alerta: ${op.alert.toFixed(1)}%</span><br/>
                    <span style="color: #DC2626;">● Alarma: ${op.alarm.toFixed(1)}%</span><br/>
                    <span style="color: #64748B;">● Fuera de serv.: ${op.down.toFixed(1)}%</span>
                </div>
            `;

            if (activePopup) activePopup.remove();
            activePopup = new maplibregl.Popup({ closeButton: false, offset: 15 })
                .setLngLat([Number(equipo.longitud), Number(equipo.latitud)])
                .setHTML(htmlContent)
                .addTo(mapInstance);
        });

        el.addEventListener("mouseleave", () => {
            if (activePopup) {
                activePopup.remove();
                activePopup = null;
            }
        });

        const marker = new maplibregl.Marker({ element: el })
            .setLngLat([Number(equipo.longitud), Number(equipo.latitud)])
            .addTo(mapInstance);

        currentMarkers.push(marker);
    });
};

watch(
    () => [props.trucks, props.operabilityData, searchQuery.value],
    () => {
        if (mapInstance) {
            renderMapMarkers();
        }
    },
    { deep: true }
);

function inicializarMapa() {
    if (!mapTruckContainer.value || mapInstance) return;

    mapInstance = new maplibregl.Map({
        container: mapTruckContainer.value,
        center: centerMap.value,
        zoom: 14,
        pitch: 35,
        bearing: 20,
        style: "https://api.maptiler.com/maps/satellite/style.json?key=6JWVt6LFaY22nquimZpF",
    });

    mapInstance.addControl(new maplibregl.NavigationControl(), "top-right");

    mapInstance.on("load", () => {
        setTimeout(() => {
            if (mapInstance) {
                mapInstance.resize();
                renderMapMarkers();
            }
        }, 300);
    });
}

function toggleFullscreen() {
    if (!wrapperRef.value) return;
    if (!document.fullscreenElement) {
        wrapperRef.value.requestFullscreen().then(() => {
            isFullscreen.value = true;
            nextTick(() => mapInstance?.resize());
        });
    } else {
        document.exitFullscreen().then(() => {
            isFullscreen.value = false;
            nextTick(() => mapInstance?.resize());
        });
    }
}

onMounted(() => {
    nextTick(() => {
        inicializarMapa();
    });
});

onUnmounted(() => {
    clearMarkers();
    if (mapInstance) mapInstance.remove();
});
</script>

<style scoped>
:deep(.neon-search-field .v-field) {
    background: rgba(13, 19, 31, 0.8) !important;
    border-radius: 8px !important;
    border: 1px solid rgba(0, 229, 255, 0.25) !important;
    transition: all 0.3s ease;
}

:deep(.neon-search-field .v-field--focused) {
    border-color: #00E5FF !important;
    box-shadow: 0 0 10px rgba(0, 229, 255, 0.3) !important;
}

:deep(.neon-search-field input) {
    font-size: 0.78rem !important;
    color: #E2E8F0 !important;
}

:deep(.neon-search-field .v-icon) {
    color: #00E5FF !important;
    font-size: 18px !important;
}

.truck-map-container {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.topology-wrapper {
    position: relative;
    width: 100%;
    height: 60vh;
    background-color: #06090e;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.05);
}

.mapTruckContainer {
    width: 100% !important;
    height: 100% !important;
}

.fullscreen-btn {
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 10;
    background-color: rgba(15, 23, 42, 0.8) !important;
    backdrop-filter: blur(4px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #FFFFFF !important;
}

.no-gps-section {
    background-color: #0c1017;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
}

.no-gps-title {
    font-size: 0.72rem;
    color: #94A3B8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.no-gps-scroll-container {
    overflow-x: auto;
    white-space: nowrap;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.no-gps-card {
    background-color: #121824;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    min-width: 160px;
    max-width: 180px;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
    flex: 0 0 auto;
}

.no-gps-card:hover {
    border-color: rgba(255, 255, 255, 0.35);
    background-color: #1a2232;
}

.device-name { font-size: 0.68rem; font-family: monospace; }
.device-model { font-size: 0.58rem; color: #94A3B8; }
.ip-text { font-family: monospace; font-size: 0.60rem; color: #64748B; }
.any-badge { font-size: 0.60rem; padding: 0 3px; border-radius: 3px; background-color: rgba(0, 0, 0, 0.5); }
.operability-bar-wrapper { background: rgba(255, 255, 255, 0.05); border-radius: 2px; overflow: hidden; }
.operability-stacked-bar { display: flex; width: 100%; height: 3px; }
.bar-segment { height: 100%; }

:deep(.custom-icon-marker) {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    user-select: none;
    transform: translate(-50%, -50%);
}

:deep(.custom-icon-marker:hover .device-img) {
    transform: scale(1.15);
}

:deep(.device-img) {
    width: 32px;
    height: 32px;
    object-fit: contain;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.8));
    transition: transform 0.2s ease;
    border-radius: 4px;
}

:deep(.marker-name-only) {
    font-family: monospace;
    font-weight: 800;
    font-size: 0.72rem;
    line-height: 1.1;
    text-align: center;
    margin-top: 3px;
    background-color: rgba(6, 9, 14, 0.75);
    padding: 1px 4px;
    border-radius: 3px;
    text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000;
}
</style>